import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, Trash2, FileText, AlertTriangle } from "lucide-react";
import { FileAzioni } from "./FileViewer";
import { toast } from "sonner";
import api, { fmtDate } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

export default function MioDocumenti({ opId, categories, docFilter, defaultCategoria, testidSuffix = "doc", title = "Documenti", obbligatori = [] }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState({});
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const { data: docs = [] } = useQuery({
    queryKey: ["op-doc-ro", opId], enabled: !!opId, retry: false,
    queryFn: async () => (await api.get(`/firma/mio-account/documenti`, { params: { op_id: opId } })).data,
  });
  const shownAll = docFilter ? docs.filter(docFilter) : docs;
  const catOf = (d) => (obbligatori.find(([, alias]) => (alias || []).includes(d.categoria))?.[0]) || d.categoria;
  const isObbl = (d) => obbligatori.some(([c]) => c === catOf(d));
  const shown = shownAll.filter((d) => !isObbl(d));
  const isExpiring = (sc) => sc && sc <= new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);

  const openUpload = (c) => { const cat = typeof c === "string" ? c : defaultCategoria; setMeta(cat ? { categoria: cat } : {}); setOpen(true); };
  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Seleziona un file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(meta).forEach(([k, v]) => v && fd.append(k, v));
      await api.post(`/firma/mio-account/documenti?op_id=${opId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      setOpen(false); setMeta({}); qc.invalidateQueries({ queryKey: ["op-doc-ro", opId] });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore caricamento"); }
    finally { setUploading(false); }
  };
  const fa = (d, size) => <FileAzioni url={`/firma/mio-account/documenti/${d.id}/download`} params={{ op_id: opId }} titolo={d.original_filename || d.titolo || "documento"} testid={`mio-doc-${d.id}`} size={size} />;
  const remove = async (id) => {
    if (!window.confirm("Eliminare questo documento?")) return;
    try { await api.delete(`/firma/mio-account/documenti/${id}`, { params: { op_id: opId } }); toast.success("Eliminato"); qc.invalidateQueries({ queryKey: ["op-doc-ro", opId] }); }
    catch { toast.error("Errore"); }
  };

  return (
    <div>
      {obbligatori.length > 0 && (
        <div className="mb-4" data-testid="doc-obbligatori">
          <div className="text-[11px] font-extrabold uppercase tracking-wider text-emerald-800 mb-2">Documento obbligatorio</div>
          <div className="grid md:grid-cols-2 gap-3">
            {obbligatori.map(([c, , hint]) => {
              const items = shownAll.filter((d) => catOf(d) === c); const ok = items.length > 0;
              return (
                <div key={c} className={`rounded-xl border-2 bg-white p-3 ${ok ? "border-emerald-300" : "border-red-300"}`} data-testid={`doc-obbl-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="flex items-center gap-2 mb-1"><b className="text-sm">{c}</b><span className={`ml-auto text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid="doc-obbl-stato">{ok ? "Caricato" : "Mancante"}</span></div>
                  {hint && <p className="text-[11px] text-muted-foreground mb-2">{hint}</p>}
                  {items.map((d) => <div key={d.id} className="flex items-center gap-2 rounded-lg border p-2 text-xs mb-1"><FileText size={14} className="text-primary shrink-0" /><span className="flex-1 truncate font-semibold">{d.titolo || d.original_filename}</span>{fa(d, 14)}</div>)}
                  <Button size="sm" variant={ok ? "outline" : "default"} className="w-full h-8 text-xs font-semibold mt-1" onClick={() => openUpload(c)} data-testid="mio-upload-obbl"><Upload size={13} className="mr-1" /> Carica</Button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><FileText size={18} className="text-primary" /><h3 className="font-head font-bold">{obbligatori.length ? "Altri documenti" : title}</h3></div>
        <Button size="sm" onClick={openUpload} data-testid={`mio-upload-${testidSuffix}`}><Upload size={15} className="mr-1" /> Carica</Button>
      </div>
      <div className="space-y-2">
        {shown.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Nessun documento caricato.</p>}
        {shown.map((d) => (
          <div key={d.id} className="flex items-center gap-3 p-3 rounded-lg border bg-white" data-testid={`mio-doc-${d.id}`}>
            <FileText size={18} className="text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{d.titolo || d.original_filename || "Documento"}</div>
              <div className="text-xs text-muted-foreground">{d.categoria || "—"} · caricato {fmtDate(d.created_at)}{d.data_scadenza && ` · scade ${fmtDate(d.data_scadenza)}`}</div>
            </div>
            {isExpiring(d.data_scadenza) && <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600"><AlertTriangle size={13} /> In scadenza</span>}
            {fa(d, 16)}
            <button onClick={() => remove(d.id)} title="Elimina" data-testid={`mio-doc-del-${d.id}`} className="p-1.5 rounded hover:bg-secondary text-destructive"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Carica {title.toLowerCase()}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold mb-1 block">File</Label><Input type="file" ref={fileRef} data-testid="mio-doc-file" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Titolo</Label><Input value={meta.titolo || ""} onChange={(e) => setMeta((p) => ({ ...p, titolo: e.target.value }))} data-testid="mio-doc-titolo" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Categoria</Label>
              <Select value={meta.categoria || ""} onValueChange={(v) => setMeta((p) => ({ ...p, categoria: v }))}>
                <SelectTrigger data-testid="mio-doc-categoria"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{(categories || ["Altro"]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs font-semibold mb-1 block">Data scadenza (opzionale)</Label><Input type="date" value={meta.data_scadenza || ""} onChange={(e) => setMeta((p) => ({ ...p, data_scadenza: e.target.value }))} data-testid="mio-doc-scadenza" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={upload} disabled={uploading} data-testid="mio-doc-save">{uploading ? "Caricamento..." : "Carica"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
